import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createFacility,
  deleteFacility,
  fetchDetailFacility,
  fetchFacilityEndpoints,
  fetchFacilityList,
  updateFacility,
} from '@/domains/asset-management/apis/facility-management';
import type {
  CreateFacilityPayload,
  CreateFacilityResponse,
  DeleteFacilityResponse,
  FacilityDetailResponse,
  FacilityEndpointResponse,
  FacilityID,
  FacilityListResponse,
  UpdateFacilityParams,
  UpdateFacilityPayload,
  UpdateFacilityResponse,
  UseFacilityManagementProps,
} from '@/domains/asset-management/types/facility';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const useFacilityManagement = ({
  fcID = null,
  queryParams = '',
  facilityType,
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  refetchInterval,
}: UseFacilityManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: facilityListData, isLoading: isLoadingFacilityList } =
    useQuery<FacilityListResponse>({
      queryKey: ['facilityListData', queryParams],
      queryFn: () => fetchFacilityList(queryParams),
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: true,
      initialData: {
        code: '-',
        message: '-',
        data: { total: 0, page: 1, size: 10, total_pages: 0, rows: [] },
      },
      initialDataUpdatedAt: 0,
    });

  const { data: facilityEndpointsData } = useQuery<FacilityEndpointResponse>({
    queryKey: ['facilityEndpoints', facilityType],
    queryFn: () => fetchFacilityEndpoints(facilityType),
    staleTime: 1000 * 60 * 5,
    enabled: !!facilityType,
  });

  // Single list query reused for both the table and dropdown consumers; previously
  // this endpoint was fetched twice (separate query keys, identical params).
  const facilityListDataManagementPage = facilityListData;

  const { data: detailFacilityData, refetch: refetchDetailFacility } = useQuery<
    FacilityDetailResponse,
    Error
  >({
    queryKey: ['detailFacilityData', fcID],
    queryFn: () => fetchDetailFacility(fcID as FacilityID),
    retry: false,
    enabled: !!fcID,
  });

  const createFacilityMutation = useMutation<CreateFacilityResponse, Error, CreateFacilityPayload>({
    mutationFn: (facilityData) => createFacility(facilityData),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['facilityListData', queryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteFacilityMutation = useMutation<DeleteFacilityResponse, Error, FacilityID>({
    mutationFn: (fcID) => deleteFacility(fcID),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['facilityListData', queryParams],
          });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateFacilityMutation = useMutation<
    UpdateFacilityResponse,
    Error,
    {
      facilityData: UpdateFacilityPayload;
      params: UpdateFacilityParams;
    }
  >({
    mutationFn: ({ facilityData, params }) => updateFacility(facilityData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['facilityListData', queryParams],
          });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    facilityListDataManagementPage,
    facilityListData,
    detailFacilityData,
    facilityEndpointsData,
    isLoadingFacilityList,
    refetchDetailFacility,
    createFacility: createFacilityMutation.mutate,
    updateFacility: updateFacilityMutation.mutate,
    deleteFacility: deleteFacilityMutation.mutate,
  };
};

export default useFacilityManagement;
